import {useCallback, useEffect, useState} from 'react';
import {getFatura, getFaturaPdf, listCobrancasDaFatura} from './FinanceiroService';
import {extrairCobrancas, getBackendMessage, normalizarDetalheDaFatura} from './financeiroUtils';

export const PDF_INDISPONIVEL = 'O PDF não está disponível agora. Tente de novo em instantes.';
export const COBRANCAS_INDISPONIVEIS = 'Não foi possível carregar as cobranças desta fatura. Tente de novo em instantes.';

/**
 * A fatura (seções, linhas e total) e as cobranças dela, lidas juntas. Sem as cobranças, a fatura
 * aparece do mesmo jeito, com um aviso no lugar delas.
 */
export function useFatura(faturaId) {
    const [detalhe, setDetalhe] = useState(null);
    const [cobrancas, setCobrancas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [erroCobrancas, setErroCobrancas] = useState('');
    const [baixandoPdf, setBaixandoPdf] = useState(false);
    const [erroPdf, setErroPdf] = useState('');

    const loadFatura = useCallback(async () => {
        setLoading(true);
        setError('');
        setErroCobrancas('');

        const [faturaResult, cobrancasResult] = await Promise.allSettled([
            getFatura(faturaId),
            listCobrancasDaFatura(faturaId),
        ]);

        if (faturaResult.status === 'fulfilled') {
            setDetalhe(normalizarDetalheDaFatura(faturaResult.value?.data?.data ?? {}));
        } else {
            setDetalhe(null);
            setError(getBackendMessage(faturaResult.reason, 'Não foi possível carregar a fatura.'));
        }

        if (cobrancasResult.status === 'fulfilled') {
            setCobrancas(extrairCobrancas(cobrancasResult.value));
        } else {
            setCobrancas([]);
            setErroCobrancas(COBRANCAS_INDISPONIVEIS);
        }

        setLoading(false);
    }, [faturaId]);

    useEffect(() => {
        loadFatura();
    }, [loadFatura]);

    // O link vale 5 minutos: é pedido a cada clique e aberto na hora, sem guardar.
    const baixarPdf = useCallback(async () => {
        setBaixandoPdf(true);
        setErroPdf('');
        try {
            const response = await getFaturaPdf(faturaId);
            const url = response?.data?.data?.url;
            if (!url) {
                setErroPdf(PDF_INDISPONIVEL);
                return;
            }
            window.open(url, '_blank', 'noopener');
        } catch (pdfError) {
            setErroPdf(pdfError?.response?.status === 502
                ? PDF_INDISPONIVEL
                : getBackendMessage(pdfError, 'Não foi possível baixar o PDF da fatura.'));
        } finally {
            setBaixandoPdf(false);
        }
    }, [faturaId]);

    return {
        detalhe,
        cobrancas,
        loading,
        error,
        erroCobrancas,
        baixandoPdf,
        erroPdf,
        baixarPdf,
        reload: loadFatura,
    };
}
